import {
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import dropdownIcon from '../../assets/icons/Dropdown.svg';
import styles from '../../styles/Dropdown.module.css';

function joinClasses(...parts) {
  return parts.filter(Boolean).join(' ');
}

function scrollParents(el) {
  const list = [window];
  while (el) {
    const s = getComputedStyle(el);
    if (
      /(auto|scroll|overlay)/.test(s.overflowY) ||
      /(auto|scroll|overlay)/.test(s.overflowX) ||
      /(auto|scroll|overlay)/.test(s.overflow)
    ) {
      list.push(el);
    }
    el = el.parentElement;
  }
  return list;
}

function toOptions(raw) {
  if (!Array.isArray(raw)) return [];
  return raw.map((o) => {
    if (o == null) return { value: '', label: '', searchText: '', primary: '', suffix: '' };
    if (typeof o === 'string') {
      return { value: o, label: o, searchText: o, primary: o, suffix: '' };
    }
    const label =
      o.label != null && o.label !== '' ? o.label : String(o.value ?? '');
    const primary =
      o.primary != null && o.primary !== '' ? String(o.primary) : label;
    const suffix = o.suffix != null ? String(o.suffix) : '';
    const searchText =
      o.searchText != null && o.searchText !== '' ? String(o.searchText) : primary;
    return {
      value: o.value,
      label,
      searchText,
      primary,
      suffix,
    };
  });
}

const PARENT_SUFFIX_STYLE = { color: '#dc2626', fontWeight: 600 };

function OptionLabelContent({ opt, suffixClassName }) {
  if (!opt.suffix) {
    return <>{opt.primary ?? opt.label}</>;
  }
  return (
    <>
      <span>{opt.primary ?? opt.label}</span>
      <span
        className={suffixClassName}
        data-parent-suffix=""
        style={PARENT_SUFFIX_STYLE}
      >
        {opt.suffix}
      </span>
    </>
  );
}

export default function Dropdown({
  options,
  value,
  onChange,
  ariaLabel,
  disabled,
  variant = 'default',
  rootClassName,
  placeholder,
  searchable = false,
  searchPlaceholder = 'Search…',
  menuMinWidth = 240,
  menuMaxWidth = 360,
  modalLarge = false,
}) {
  const listboxId = useId();
  const buttonId = useId();
  const searchId = useId();
  const rootRef = useRef(null);
  const anchorRef = useRef(null);
  const menuRef = useRef(null);
  const searchRef = useRef(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const rows = toOptions(options);
  const visibleRows = useMemo(() => {
    if (!searchable) return rows;
    const q = searchQuery.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((o) =>
      String(o.searchText ?? o.label ?? '')
        .toLowerCase()
        .includes(q),
    );
  }, [rows, searchable, searchQuery]);
  const valueKey = value == null || value === '' ? '' : String(value);
  const matched = rows.find((o) => String(o.value ?? '') === valueKey);
  const selectedLabel =
    matched?.label ?? (valueKey === '' && placeholder ? placeholder : '');
  const selectedTitle =
    matched?.suffix
      ? `${matched.primary ?? matched.label}${matched.suffix}`
      : selectedLabel;
  const showingPlaceholder = valueKey === '' && Boolean(placeholder) && !matched;
  const triggerSuffixClass =
    variant === 'modal' ? styles.modalTriggerSuffix : styles.triggerSuffix;
  const optionSuffixClass = styles.optionSuffix;

  let shellClass = styles.triggerShell;
  let btnClass = styles.trigger;
  let labelClass = styles.triggerLabel;
  let iconClass = styles.dropdownIcon;
  if (variant === 'info') {
    shellClass = joinClasses(styles.triggerShell, styles.triggerShellInfo);
    btnClass = joinClasses(styles.trigger, styles.triggerInfo);
  } else if (variant === 'infoReverse') {
    shellClass = joinClasses(styles.triggerShell, styles.triggerShellInfoReverse);
    btnClass = joinClasses(styles.trigger, styles.triggerInfoReverse);
  } else if (variant === 'modal') {
    shellClass = styles.triggerShellModal;
    btnClass = styles.triggerModal;
    labelClass = joinClasses(
      styles.modalTriggerLabel,
      showingPlaceholder && styles.modalTriggerLabelPlaceholder,
    );
    iconClass = styles.modalDropdownIcon;
  } else if (showingPlaceholder) {
    labelClass = joinClasses(labelClass, styles.triggerLabelPlaceholder);
  }

  const placeMenu = () => {
    const el = anchorRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const vw = typeof window !== 'undefined' ? window.innerWidth : 1200;
    const minMenu = Number(menuMinWidth) > 0 ? Number(menuMinWidth) : 240;
    const maxMenu = Math.min(
      Number(menuMaxWidth) > 0 ? Number(menuMaxWidth) : 360,
      vw - 16,
    );
    const width = Math.min(maxMenu, Math.max(minMenu, r.width));
    const left = Math.min(Math.max(8, r.left), Math.max(8, vw - width - 8));
    setMenuPosition({ top: r.bottom + 8, left, width });
  };

  useLayoutEffect(() => {
    if (!dropdownOpen) {
      setMenuPosition(null);
      setSearchQuery('');
      return;
    }
    placeMenu();
    if (searchable) {
      requestAnimationFrame(() => searchRef.current?.focus());
    }
  }, [dropdownOpen, searchable, menuMinWidth, menuMaxWidth]);

  useEffect(() => {
    if (!dropdownOpen) return;

    const onMove = () => placeMenu();
    const parents = scrollParents(anchorRef.current);
    parents.forEach((t) =>
      t.addEventListener('scroll', onMove, { passive: true, capture: true })
    );
    window.addEventListener('resize', onMove);
    const vv = window.visualViewport;
    vv?.addEventListener('resize', onMove);
    vv?.addEventListener('scroll', onMove);

    return () => {
      parents.forEach((t) => t.removeEventListener('scroll', onMove, { capture: true }));
      window.removeEventListener('resize', onMove);
      vv?.removeEventListener('resize', onMove);
      vv?.removeEventListener('scroll', onMove);
    };
  }, [dropdownOpen, menuMinWidth, menuMaxWidth]);

  useEffect(() => {
    if (!dropdownOpen) return;

    const closeIfOutside = (e) => {
      const t = e.target;
      if (!(t instanceof Node)) return;
      if (rootRef.current?.contains(t)) return;
      if (menuRef.current?.contains(t)) return;
      setDropdownOpen(false);
    };

    const onKey = (e) => {
      if (e.key === 'Escape') setDropdownOpen(false);
    };

    window.addEventListener('pointerdown', closeIfOutside);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('pointerdown', closeIfOutside);
      window.removeEventListener('keydown', onKey);
    };
  }, [dropdownOpen]);

  const body = typeof document !== 'undefined' ? document.body : null;

  const menu =
    dropdownOpen && menuPosition && body
      ? createPortal(
          <div
            ref={menuRef}
            role="presentation"
            className={styles.menuPortal}
            style={{
              top: menuPosition.top,
              left: menuPosition.left,
              width: menuPosition.width,
            }}
          >
            <div className={styles.menuPanel}>
              {searchable ? (
                <div className={styles.menuSearchWrap}>
                  <input
                    ref={searchRef}
                    id={searchId}
                    type="search"
                    className={styles.menuSearch}
                    value={searchQuery}
                    placeholder={searchPlaceholder}
                    aria-label={searchPlaceholder}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => e.stopPropagation()}
                  />
                </div>
              ) : null}
              <ul
                id={listboxId}
                className={joinClasses(styles.list, searchable && styles.listWithSearch)}
                role="listbox"
                aria-labelledby={buttonId}
                aria-label={ariaLabel}
              >
                {searchable && visibleRows.length === 0 ? (
                  <li className={styles.menuEmpty}>No matches found</li>
                ) : null}
                {visibleRows.map((opt) => {
                const picked = String(opt.value ?? '') === valueKey;
                const key =
                  opt.value === '' || opt.value == null ? '__all__' : String(opt.value);
                return (
                  <li key={key} className={styles.optionItem}>
                    <div
                      className={joinClasses(
                        styles.optionShell,
                        picked && styles.optionSelected
                      )}
                    >
                      <button
                        type="button"
                        className={styles.option}
                        role="option"
                        aria-selected={picked}
                        onClick={() => {
                          onChange(opt.value);
                          setSearchQuery('');
                          setDropdownOpen(false);
                        }}
                      >
                        <OptionLabelContent
                          opt={opt}
                          suffixClassName={optionSuffixClass}
                        />
                      </button>
                    </div>
                  </li>
                );
              })}
              </ul>
            </div>
          </div>,
          body
        )
      : null;

  return (
    <div
      ref={rootRef}
      className={joinClasses(
        styles.root,
        variant === 'modal' && styles.rootModal,
        variant === 'modal' && modalLarge && styles.rootModalLarge,
        rootClassName,
      )}
    >
      <div ref={anchorRef} className={styles.anchor}>
        <div className={shellClass}>
          <button
            id={buttonId}
            type="button"
            className={btnClass}
            aria-label={ariaLabel}
            aria-haspopup="listbox"
            aria-expanded={dropdownOpen}
            aria-controls={listboxId}
            disabled={disabled}
            onClick={() => setDropdownOpen((v) => !v)}
          >
            <span
              className={labelClass}
              title={!showingPlaceholder ? selectedTitle : undefined}
            >
              {showingPlaceholder ? (
                selectedLabel
              ) : (
                <OptionLabelContent
                  opt={matched ?? { label: selectedLabel, primary: selectedLabel, suffix: '' }}
                  suffixClassName={triggerSuffixClass}
                />
              )}
            </span>
            <span
              className={joinClasses(
                styles.dropdownArrow,
                dropdownOpen && styles.dropdownArrowOpen
              )}
              aria-hidden
            >
              <img src={dropdownIcon} alt="" className={iconClass} />
            </span>
          </button>
        </div>
      </div>

      {menu}
    </div>
  );
}
